import React from "react";
import { Image as KonvaImage, Text, Rect, Line, Group } from "react-konva";
import Konva from "konva";
import { EditorObject, EditorTool } from "../types/editorTypes";

// ハンドラーの型定義
export interface ObjectHandlers {
  setSelectedId: (id: string) => void;
  setObjects: React.Dispatch<React.SetStateAction<EditorObject[]>>;
  handleTextDoubleClick: (obj: EditorObject) => void;
  handleRectDoubleClick: (obj: EditorObject) => void;
  handleDragEnd: (id: string, e: Konva.KonvaEventObject<DragEvent>) => void;
  editingTextId: string | null;
  editingTextValue: string;
  cursorVisible: boolean;
  objects: EditorObject[];
  loadedImages: { [key: string]: HTMLImageElement };
  refreshTransformer?: () => void;
}

// 描画プレビュー用のProps
export interface DrawingPreviewProps {
  isDrawing: boolean;
  currentPoints: number[];
  currentTool: EditorTool;
  markerColor: string;
  markerStrokeWidth: number;
  rectFillEnabled: boolean;
  rectFillColor: string;
  rectStrokeEnabled: boolean;
  rectStrokeColor: string;
}

/**
 * テキストオブジェクトのレンダリング
 */
export const renderTextObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const {
    setSelectedId,
    setObjects,
    handleTextDoubleClick,
    handleDragEnd,
    editingTextId,
    objects,
    refreshTransformer,
  } = handlers;

  return (
    <Group
      key={obj.id}
      id={obj.id}
      x={obj.x}
      y={obj.y}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDblClick={() => handleTextDoubleClick(obj)}
      onDblTap={() => handleTextDoubleClick(obj)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(50, (obj.width || 150) * scaleX);
        const newHeight = Math.max(20, (obj.height || 24) * scaleY);

        // スケールをリセット
        node.scaleX(1);
        node.scaleY(1);

        // Group内の子要素のサイズを直接更新
        node.find('Rect').forEach((rect: any) => {
          rect.width(newWidth);
          rect.height(newHeight);
        });
        const text = node.findOne('Text');
        if (text) {
          text.width(newWidth);
        }

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));

        // Transformerを更新（状態更新後に実行）
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    >
      {/* サイズ定義用の透明なRect（常に表示、Transformerがサイズを認識するため必要） */}
      <Rect
        x={0}
        y={0}
        width={obj.width || 150}
        height={obj.height || 24}
        fill={obj.backgroundColor && obj.backgroundColor !== 'transparent' ? obj.backgroundColor : 'transparent'}
        stroke={obj.stroke}
        strokeWidth={obj.strokeWidth}
      />
      {/* テキスト（編集中は非表示） */}
      {obj.id !== editingTextId && (
        <Text
          x={0}
          y={0}
          text={obj.text || ''}
          fontSize={obj.fontSize}
          fontFamily={obj.fontFamily || 'sans-serif'}
          fontStyle={obj.fontStyle || 'normal'}
          align={obj.textAlign || 'left'}
          verticalAlign={obj.verticalAlign || 'top'}
          fill={obj.textColor}
          width={obj.width || 150}
          height={obj.height || 24}
          wrap="char"
        />
      )}
    </Group>
  );
};

/**
 * 四角形オブジェクトのレンダリング
 */
export const renderRectObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const {
    setSelectedId,
    setObjects,
    handleRectDoubleClick,
    handleDragEnd,
    editingTextId,
    editingTextValue,
    cursorVisible,
    objects,
    refreshTransformer,
  } = handlers;

  return (
    <Group
      key={obj.id}
      id={obj.id}
      x={obj.x}
      y={obj.y}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDblClick={() => handleRectDoubleClick(obj)}
      onDblTap={() => handleRectDoubleClick(obj)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(5, (obj.width || 0) * scaleX);
        const newHeight = Math.max(5, (obj.height || 0) * scaleY);

        // スケールをリセット
        node.scaleX(1);
        node.scaleY(1);

        // Group内の子要素のサイズを直接更新
        const rect = node.findOne('Rect');
        if (rect) {
          rect.width(newWidth);
          rect.height(newHeight);
        }
        const text = node.findOne('Text');
        if (text) {
          text.width(newWidth - 8);
          text.height(newHeight - 8);
        }

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));

        // Transformerを更新（状態更新後に実行）
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    >
      <Rect
        x={0}
        y={0}
        width={obj.width}
        height={obj.height}
        fill={obj.backgroundColor}
        stroke={obj.stroke}
        strokeWidth={obj.strokeWidth}
      />
      {(obj.id === editingTextId || obj.text) && (
        <Text
          x={4}
          y={4}
          text={obj.id === editingTextId
            ? (editingTextValue + (cursorVisible ? '|' : ''))
            : obj.text}
          fontSize={obj.fontSize || 14}
          fontFamily={obj.fontFamily || 'sans-serif'}
          fontStyle={obj.fontStyle || 'normal'}
          align={obj.textAlign || 'left'}
          verticalAlign={obj.verticalAlign || 'top'}
          fill={obj.textColor || '#000000'}
          width={(obj.width || 100) - 8}
          height={(obj.height || 50) - 8}
          wrap="char"
        />
      )}
    </Group>
  );
};

/**
 * 線/マーカーオブジェクトのレンダリング
 */
export const renderLineObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode => {
  const { setSelectedId, setObjects, handleDragEnd, objects } = handlers;

  return (
    <Line
      key={obj.id}
      id={obj.id}
      points={obj.points}
      stroke={obj.stroke}
      strokeWidth={obj.strokeWidth}
      lineCap="round"
      lineJoin="round"
      opacity={obj.type === 'marker' ? 0.5 : 1}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              rotation: node.rotation(),
            };
          }
          return o;
        }));
      }}
    />
  );
};

/**
 * 画像オブジェクトのレンダリング
 */
export const renderImageObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode | null => {
  const { setSelectedId, setObjects, handleDragEnd, objects, loadedImages, refreshTransformer } = handlers;

  if (!loadedImages[obj.id]) {
    return null;
  }

  return (
    <KonvaImage
      key={obj.id}
      id={obj.id}
      image={loadedImages[obj.id]}
      x={obj.x}
      y={obj.y}
      width={obj.width}
      height={obj.height}
      rotation={obj.rotation || 0}
      draggable
      onClick={() => setSelectedId(obj.id)}
      onTap={() => setSelectedId(obj.id)}
      onDragEnd={(e) => handleDragEnd(obj.id, e)}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const newWidth = Math.max(10, (obj.width || 0) * scaleX);
        const newHeight = Math.max(10, (obj.height || 0) * scaleY);

        // スケールをリセットしてサイズを直接設定
        node.scaleX(1);
        node.scaleY(1);
        node.width(newWidth);
        node.height(newHeight);

        setObjects(objects.map(o => {
          if (o.id === obj.id) {
            return {
              ...o,
              x: node.x(),
              y: node.y(),
              width: newWidth,
              height: newHeight,
              rotation: node.rotation(),
            };
          }
          return o;
        }));
        // Transformerを更新
        setTimeout(() => refreshTransformer?.(), 0);
      }}
    />
  );
};

/**
 * オブジェクトをタイプに応じてレンダリング
 */
export const renderEditorObject = (
  obj: EditorObject,
  handlers: ObjectHandlers
): React.ReactNode | null => {
  switch (obj.type) {
    case 'text':
      return renderTextObject(obj, handlers);
    case 'rect':
      return renderRectObject(obj, handlers);
    case 'line':
    case 'marker':
      return renderLineObject(obj, handlers);
    case 'image':
      return renderImageObject(obj, handlers);
    default:
      return null;
  }
};

/**
 * 描画中のプレビューをレンダリング
 */
export const renderDrawingPreview = (props: DrawingPreviewProps): React.ReactNode | null => {
  const {
    isDrawing,
    currentPoints,
    currentTool,
    markerColor,
    markerStrokeWidth,
    rectFillEnabled,
    rectFillColor,
    rectStrokeEnabled,
    rectStrokeColor,
  } = props;

  if (!isDrawing || currentPoints.length < 2) {
    return null;
  }

  return (
    <>
      {currentTool === 'marker' && (
        <Line
          points={currentPoints}
          stroke={markerColor}
          strokeWidth={markerStrokeWidth}
          lineCap="round"
          lineJoin="round"
          opacity={0.5}
        />
      )}
      {currentTool === 'horizontal' && currentPoints.length >= 4 && (
        <Line
          points={[currentPoints[0], currentPoints[1], currentPoints[2], currentPoints[1]]}
          stroke="#000000"
          strokeWidth={2}
        />
      )}
      {currentTool === 'rectangle' && currentPoints.length >= 4 && (
        <Rect
          x={Math.min(currentPoints[0], currentPoints[2])}
          y={Math.min(currentPoints[1], currentPoints[3])}
          width={Math.abs(currentPoints[2] - currentPoints[0])}
          height={Math.abs(currentPoints[3] - currentPoints[1])}
          fill={rectFillEnabled ? rectFillColor : undefined}
          stroke={rectStrokeEnabled ? rectStrokeColor : '#3b82f6'}
          strokeWidth={2}
          dash={rectStrokeEnabled ? undefined : [5, 5]}
          opacity={0.7}
        />
      )}
      {currentTool === 'text' && currentPoints.length >= 4 && (
        <Rect
          x={Math.min(currentPoints[0], currentPoints[2])}
          y={Math.min(currentPoints[1], currentPoints[3])}
          width={Math.abs(currentPoints[2] - currentPoints[0])}
          height={Math.abs(currentPoints[3] - currentPoints[1])}
          fill="transparent"
          stroke="#2563eb"
          strokeWidth={1}
          dash={[5, 5]}
          opacity={0.8}
        />
      )}
    </>
  );
};
